/**
 * String templating with escaping by default.
 *
 * WHY THIS EXISTS RATHER THAN A FRAMEWORK
 *   Every string this application renders that did not come from our own code is
 *   operator-authored: skill titles, descriptions, draft bodies, change reasons,
 *   proposal summaries, audit metadata. `innerHTML` with interpolation is an
 *   injection of exactly that content into the reviewing screen, which is the one
 *   place a reviewer must be able to trust what they read. So the default is
 *   escape, and the only way to emit markup is `raw()`, which is a visible,
 *   greppable decision at the call site.
 *
 * The module is pure and has no DOM dependency, so it is testable without a
 * browser.
 */

const ESCAPES: Readonly<Record<string, string>> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

export function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (character) => ESCAPES[character] ?? character);
}

/** A string that has already been escaped, or is markup we generated. */
export class RawHtml {
  constructor(readonly value: string) {}
}

export function raw(value: string): RawHtml {
  return new RawHtml(value);
}

function interpolate(value: unknown): string {
  if (value === null || value === undefined || value === false || value === true) return '';
  if (value instanceof RawHtml) return value.value;
  if (Array.isArray(value)) return value.map(interpolate).join('');
  return escapeHtml(String(value));
}

/**
 * Tagged template that escapes every interpolated value, and returns `RawHtml`.
 *
 * The return type is the whole point. An earlier version returned `string`, which
 * meant that composing one `html` call inside another — a shared component
 * inside a view, a tag inside a header — re-escaped the inner result. The screen
 * then showed `<span class="ref">Section</span>` as visible characters. Fixing
 * that at each call site is unwinnable: it is easy to convert a return type and
 * miss an inner interpolation, and the miss is silent.
 *
 * Returning `RawHtml` makes composition correct by construction. `interpolate`
 * already passes `RawHtml` through untouched, so nesting needs no special
 * handling anywhere. A value reaches `escapeHtml` only if it is genuinely a
 * string of text, which is exactly the case escaping exists for.
 *
 * Arrays are flattened and joined with no separator, which is what view modules
 * want when they map a collection straight into markup. `null`, `undefined` and
 * booleans render as nothing, so a conditional expression reads as prose.
 */
export function html(strings: TemplateStringsArray, ...values: unknown[]): RawHtml {
  let out = strings[0] ?? '';
  for (let index = 0; index < values.length; index += 1) {
    out += interpolate(values[index]) + (strings[index + 1] ?? '');
  }
  return new RawHtml(out);
}

/** Join the truthy parts of a class attribute. */
export function cls(...parts: readonly (string | false | null | undefined)[]): string {
  return parts.filter((part): part is string => typeof part === 'string' && part !== '').join(' ');
}

/**
 * An attribute value, or nothing at all when the attribute is absent.
 *
 * `false` and `null` remove the attribute rather than rendering `false`, which
 * is the difference between `<button disabled>` and `<button disabled="false">`.
 */
export function attr(name: string, value: string | number | false | null | undefined): string {
  if (value === false || value === null || value === undefined) return '';
  return ` ${name}="${escapeHtml(String(value))}"`;
}